/**
 * Renders a ScopeEnvelope as model · instance · scenario · tag rows.
 * Used by plan_confirmation_card (frozen scope), scope_proposal_card
 * (added/removed/new_scope), and result_card's plan-parameters footer.
 *
 * Read-only display. Scope changes happen via the Scope Panel (A) or
 * the change_scope orchestration tool, never inside a card.
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

interface ScopeEntryLike {
  model_instance_id?: number;
  scenario_id?: number;
  tag?: string | null;
  forecast_model_name?: string | null;
  model_instance_name?: string | null;
  scenario_name?: string | null;
}

interface ScopeEnvelopeLike {
  entries?: ScopeEntryLike[];
}

interface ScopeBlockProps {
  /** A ScopeEnvelope-shaped dict from the card payload. */
  scope: unknown;
  /** Optional left-edge accent color for added/removed visualization. */
  accent?: 'added' | 'removed' | 'none';
}

export const ScopeBlock: React.FC<ScopeBlockProps> = ({
  scope,
  accent = 'none',
}) => {
  const entries = extractEntries(scope);
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        (empty scope)
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {entries.map((entry, i) => (
        <ScopeEntryRow key={i} entry={entry} accent={accent} />
      ))}
    </Stack>
  );
};

const ScopeEntryRow: React.FC<{
  entry: ScopeEntryLike;
  accent: 'added' | 'removed' | 'none';
}> = ({ entry, accent }) => {
  const accentColor =
    accent === 'added'
      ? 'success.main'
      : accent === 'removed'
        ? 'error.main'
        : null;
  const prefix = accent === 'added' ? '+ ' : accent === 'removed' ? '− ' : '';

  // Prefer names when available; fall back to ids in monospace.
  const modelLabel = entry.forecast_model_name ?? null;
  const instanceLabel = entry.model_instance_name ?? null;
  const scenarioLabel = entry.scenario_name ?? null;
  const tag = entry.tag ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.5,
        pl: accentColor ? 1 : 0,
        borderLeft: accentColor ? '2px solid' : 'none',
        borderColor: accentColor || 'transparent',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          color: 'text.primary',
          fontFamily: !modelLabel ? 'monospace' : undefined,
        }}
      >
        {prefix}
        {modelLabel ?? `model_instance=${entry.model_instance_id ?? '?'}`}
      </Typography>
      {instanceLabel || scenarioLabel ? (
        <Typography
          component="span"
          sx={{ fontSize: 13, color: 'text.secondary' }}
        >
          {' · '}
          {instanceLabel ?? ''}
          {instanceLabel && scenarioLabel ? ' · ' : ''}
          {scenarioLabel ?? `scenario=${entry.scenario_id ?? '?'}`}
        </Typography>
      ) : null}
      {tag ? (
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            ml: 0.5,
            fontStyle: 'italic',
          }}
        >
          {tag}
        </Typography>
      ) : null}
    </Box>
  );
};

function extractEntries(scope: unknown): ScopeEntryLike[] {
  if (!scope || typeof scope !== 'object') return [];
  const env = scope as ScopeEnvelopeLike;
  if (!Array.isArray(env.entries)) return [];
  return env.entries.filter(
    (e): e is ScopeEntryLike => e !== null && typeof e === 'object'
  );
}
